import { useInfiniteQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'

import { StoresContext } from '../../../provider/StoreProvider'

import { getDisclosureList, getDisclosureCategories } from '../../../api/disclosure'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { AlignCenterItem, CenterWrap } from '../../../components/common'
import { Header } from '../../../components/header'
import { Category } from '../../../components/paramOption/Category'
import { InfinityScroll } from '../../../components/pagingList/InfinityScroll'
import { NotificationList, NotificationItem } from '../../notification/components/List'
import { Spinner } from '../../../components/spinner/Spinner'
import { StyledLabel } from '../../../components/label'
import { Head } from '../../../components/head/Head'
import { Text } from '../../../components/text'

const HEAD_INFO = {
  title: '공시 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '공시 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function Disclosure() {
  const {
    commonStore: { setShowTab },
  } = useContext(StoresContext)

  const [categoryId, setCategoryId] = useState<number | undefined>(undefined)

  const {
    data: categoryData,
    isPending: isCategoryPending,
    isError: isCategoryError,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY.disclosureCategories],
    queryFn: async () => await getDisclosureCategories({ offset: 0, size: 50 }),
    getNextPageParam: () => undefined,
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })
  const { data, isPending, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.disclosure, { categoryId }],
    queryFn: async ({ pageParam = 0 }) => await getDisclosureList({ offset: pageParam, size: 10, categoryId }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    setShowTab(false)
  }, [])

  if (isPending || isError || isCategoryPending || isCategoryError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="공시" />
        <StyledContent id="infinity-scroll-target">
          <Category
            categoryList={categoryData.pages}
            categoryId={categoryId}
            setCategoryId={(id) => setCategoryId(id === -1 ? undefined : id)}
          />
          <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
            <NotificationList>
              {data.pages.map(({ id, categoryId, title, createdDate }) => {
                const categoryName = categoryData.pages.find((v) => v.id === categoryId)?.name
                const isNew = dayjs().diff(dayjs(createdDate), 'day') + 1 < 8

                return (
                  <Link key={id} to={`/disclosure/${id}`}>
                    <NotificationItem>
                      <AlignCenterItem className="notification-type" $gap={6}>
                        <NotificationItem.Type>{categoryName ?? '공시'}</NotificationItem.Type>
                        {isNew ? (
                          <StyledLabel backgroundColor="error_light">
                            <Text size={12} weight={500} color="error">
                              NEW
                            </Text>
                          </StyledLabel>
                        ) : null}
                      </AlignCenterItem>
                      <NotificationItem.Title>{title}</NotificationItem.Title>
                      <NotificationItem.Date>{createdDate}</NotificationItem.Date>
                    </NotificationItem>
                  </Link>
                )
              })}
            </NotificationList>
          </InfinityScroll>
        </StyledContent>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  > div:first-child {
    margin: 16px 0 8px;
  }
`
